import type { Reservation, Customer, Table } from '../types/reservation';

export type NotificationEventType =
  | 'NEW_RESERVATION_ONLINE'
  | 'NEW_RESERVATION_MANUAL'
  | 'CANCELLATION'
  | 'MODIFICATION'
  | 'NO_SHOW_FLAGGED'
  | 'CUSTOMER_ARRIVED'
  | 'TABLE_FREED';

export type NotificationSource = 'online' | 'manual' | 'phone' | 'walk-in' | 'unknown';

export interface NotificationEvent {
  id: string;
  reservationId: string;
  type: NotificationEventType;
  source: NotificationSource;
  shiftId?: string;
  notes?: string;
  timestamp: string;
  title: string;
  description: string;
  customerName: string;
  partySize: number;
  reservationDate: string;
  reservationTime: string;
  tableLabel: string;
}

export interface NotificationFilters {
  type: 'all' | NotificationEventType;
  source: 'all' | NotificationSource;
  dateRange: 'today' | 'last7days' | 'last30days' | 'all';
  readState: 'all' | 'unread';
}

const onlineRegex = /(online|web|site|preview|internet|booking\s*form)/i;
const phoneRegex = /(phone|τηλ|τηλεφων)/i;
const walkInRegex = /(walk-?in|χωρις\s*κρατηση)/i;

export function inferNotificationSource(reservation: Reservation): NotificationSource {
  // 1) Primary signal: reservation.source column from the database.
  //    This is set explicitly by the app / edge functions (e.g. 'online' from public booking).
  const rawSource = (reservation as any).source as string | undefined;
  if (rawSource) {
    const normalized = rawSource.toLowerCase();
    if (normalized === 'online') return 'online';
    if (normalized === 'phone') return 'phone';
    if (normalized === 'walk-in' || normalized === 'walkin') return 'walk-in';
    if (normalized === 'manual') return 'manual';
  }

  // 2) Fallback: try to infer from free‑text notes for legacy data.
  const notes = reservation.notes || '';
  if (onlineRegex.test(notes)) return 'online';
  if (phoneRegex.test(notes)) return 'phone';
  if (walkInRegex.test(notes)) return 'walk-in';

  // 3) Default when we have no better signal.
  return 'manual';
}

function getTableLabel(tableId: string | undefined, tablesById: Map<string, Table>): string {
  if (!tableId) return 'Χωρίς ανάθεση';
  const table = tablesById.get(tableId);
  if (!table) return 'Τραπέζι -';
  return `Τραπέζι ${table.number}`;
}

function getCustomerName(customerId: string, customersById: Map<string, Customer>): string {
  return customersById.get(customerId)?.name || 'Άγνωστος πελάτης';
}

export function createNotificationEvent(params: {
  reservation: Reservation;
  type: NotificationEventType;
  tablesById: Map<string, Table>;
  customersById: Map<string, Customer>;
  timestamp?: string;
}): NotificationEvent {
  const { reservation, type, tablesById, customersById } = params;
  const timestamp = params.timestamp || reservation.updated_at || reservation.created_at;
  const source = inferNotificationSource(reservation);
  const tableLabel = getTableLabel(reservation.table_id, tablesById);
  const customerName = getCustomerName(reservation.customer_id, customersById);
  const timeLabel = reservation.time ? reservation.time.slice(0, 5) : '';

  const base = {
    // Use a stable identifier so read/unread state can persist across sessions.
    // We key by reservation + event type; the timestamp is still stored separately
    // for sorting and display, but no longer affects the ID.
    id: `${reservation.id}-${type}`,
    reservationId: reservation.id,
    type,
    source,
    shiftId: (reservation as any).shift_id || undefined,
    notes: reservation.notes || undefined,
    timestamp,
    customerName,
    partySize: reservation.party_size,
    reservationDate: reservation.date,
    reservationTime: reservation.time,
    tableLabel,
  };

  switch (type) {
    case 'NEW_RESERVATION_ONLINE':
      return {
        ...base,
        title: 'Νέα online κράτηση',
        description: `${customerName} • ${reservation.party_size} άτομα • ${timeLabel || 'Χωρίς ώρα'} • ${tableLabel}`,
      };
    case 'NEW_RESERVATION_MANUAL':
      return {
        ...base,
        title: 'Νέα κράτηση',
        description: `${customerName} • ${reservation.party_size} άτομα • ${timeLabel || 'Χωρίς ώρα'} • ${tableLabel}`,
      };
    case 'CANCELLATION':
      return {
        ...base,
        title: 'Ακύρωση κράτησης',
        description: `${customerName} • ${reservation.party_size} άτομα • ${timeLabel || '—'}`,
      };
    case 'MODIFICATION':
      return {
        ...base,
        title: 'Τροποποίηση κράτησης',
        description: `${customerName} • Νέα ώρα: ${timeLabel || '—'} • ${tableLabel}`,
      };
    case 'NO_SHOW_FLAGGED':
      return {
        ...base,
        title: 'Σήμανση no-show',
        description: `${customerName} • ${reservation.party_size} άτομα`,
      };
    case 'CUSTOMER_ARRIVED':
      return {
        ...base,
        title: 'Άφιξη πελάτη',
        description: `${customerName} • ${tableLabel}`,
      };
    case 'TABLE_FREED':
      return {
        ...base,
        title: 'Απελευθέρωση τραπεζιού',
        description: `${tableLabel} • ${customerName}`,
      };
    default:
      return {
        ...base,
        title: 'Ενημέρωση κράτησης',
        description: `${customerName} • ${timeLabel || '—'}`,
      };
  }
}

function hasReservationCoreChange(previous: Reservation, next: Reservation): boolean {
  const normalizeTime = (value: string | undefined): string => {
    if (!value) return '';
    return value.length >= 5 ? value.slice(0, 5) : value;
  };

  return (
    previous.date !== next.date ||
    normalizeTime(previous.time) !== normalizeTime(next.time) ||
    previous.party_size !== next.party_size ||
    (previous.table_id || '') !== (next.table_id || '')
  );
}

export function getEventTypeFromReservationChange(
  previous: Reservation | undefined,
  next: Reservation
): NotificationEventType | null {
  if (!previous) {
    return inferNotificationSource(next) === 'online'
      ? 'NEW_RESERVATION_ONLINE'
      : 'NEW_RESERVATION_MANUAL';
  }

  if (previous.status !== next.status) {
    if (next.status === 'cancelled') return 'CANCELLATION';
    if (next.status === 'no-show') return 'NO_SHOW_FLAGGED';
    if (next.status === 'in-progress') return 'CUSTOMER_ARRIVED';
    if (next.status === 'completed' && previous.table_id && !next.table_id) return 'TABLE_FREED';
  }

  if (hasReservationCoreChange(previous, next)) {
    return 'MODIFICATION';
  }

  return null;
}

export function compareByNewest(a: NotificationEvent, b: NotificationEvent): number {
  return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
}

export function filterNotifications(
  events: NotificationEvent[],
  filters: NotificationFilters,
  readIds: Set<string>
): NotificationEvent[] {
  const now = new Date();

  return events.filter((event) => {
    if (filters.type !== 'all' && event.type !== filters.type) return false;
    if (filters.source !== 'all' && event.source !== filters.source) return false;
    if (filters.readState === 'unread' && readIds.has(event.id)) return false;

    if (filters.dateRange !== 'all') {
      const eventDate = new Date(event.timestamp);
      if (filters.dateRange === 'today') {
        const sameDay =
          eventDate.getFullYear() === now.getFullYear() &&
          eventDate.getMonth() === now.getMonth() &&
          eventDate.getDate() === now.getDate();
        if (!sameDay) return false;
      }
      if (filters.dateRange === 'last7days') {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        if (eventDate < sevenDaysAgo) return false;
      }
      if (filters.dateRange === 'last30days') {
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(now.getDate() - 30);
        if (eventDate < thirtyDaysAgo) return false;
      }
    }

    return true;
  });
}

export function formatRelativeTime(timestamp: string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMins < 1) return 'Τώρα';
  if (diffMins < 60) return `Πριν ${diffMins} λεπτά`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Πριν ${diffHours} ώρες`;
  const diffDays = Math.floor(diffHours / 24);
  return `Πριν ${diffDays} ημέρες`;
}

export function getDayGroupLabel(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDiff = Math.floor((startOfToday - startOfDate) / 86400000);

  if (dayDiff === 0) return 'Σήμερα';
  if (dayDiff === 1) return 'Χθες';
  return date.toLocaleDateString('el-GR');
}
